import Spinner from '../components/Spinner';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sources } from '../api/sources';
import VerifyEligibilityForm from '../components/VerifyEligibilityForm';
import { Pages } from '../shared/pages';
import { useGlobalState } from '../shared/state';
import { autoPayPath } from '../shared/constants';
import CookieHelper from '../utils/cookieHelper';
import { UtagHelper } from '../utils/utagHelper';
import { AppFlow, IVerifyEligibilityRequest } from '@lib/types';
import { BrokerRegion } from '@ace-clublabs/payment-types';
import { ErrorType } from '../shared/types';
import StatusCode from 'status-code-enum';

const VerifyEligibilityPage: React.FC = () => {
  const [, setAmountDue] = useGlobalState('amountDue');
  const [membershipNumber, setMembershipNumber] = useGlobalState('membershipNumber');
  const [brokerRegion, setBrokerRegion] = useGlobalState('brokerRegion');
  const [, setMembershipLevel] = useGlobalState('membershipLevel');
  const [, setCampaignCode] = useGlobalState('campaignCode');
  const [zipCode, setZipCode] = useGlobalState('zipCode');
  const [, setAutoPay] = useGlobalState('autoPay');
  const [, setEmailOnFile] = useGlobalState('emailOnFile');
  const [, setProductCost] = useGlobalState('productCost');
  const [, setPreDiscountAmountDue] = useGlobalState('preDiscountAmountDue');
  const [, setTaxAmount] = useGlobalState('taxAmount');
  const [, setGlobalClubCode] = useGlobalState('clubCode');
  const [, setBillPlan] = useGlobalState('billPlan');
  const [, setCustomerId] = useGlobalState('customerId');
  const [appFlow, setAppFlow] = useGlobalState('appFlow');
  const [isLoading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const [errorType, setErrorType] = useState(undefined as undefined | ErrorType);
  const navigate = useNavigate();
  const [clubCode, setClubCode] = useState('004');
  const [tagging, setIsTagging] = useState(true);
  const [, setMembershipProductRV] = useGlobalState('membershipProductRV');
  const setClubCodeFromCookie = () => setClubCode(CookieHelper.getClubCodeFromZipcodeCookie() || '004');

  useEffect(() => {
    const { pathname } = window?.location;
    const _appFlow = pathname?.startsWith(autoPayPath) ? AppFlow.AUTOPAY : AppFlow.PAYMENT;
    setAppFlow(_appFlow);
    const params = new URLSearchParams(window.location.search);
    const brokerRegion = (params.get('backend') as BrokerRegion)?.toUpperCase() || undefined;
    if (brokerRegion === BrokerRegion.A1 || brokerRegion === BrokerRegion.B1) {
      setBrokerRegion(brokerRegion);
    }
    setCustomerId(undefined);

    UtagHelper.tag({
      ...UtagHelper.CommonTaggingValues(_appFlow),
      club: '',
      page: UtagHelper.Pages(_appFlow).verifyEligibility,
    });
    setIsTagging(false);
    setClubCodeFromCookie();
    document.addEventListener('zipRouteUpdate', setClubCodeFromCookie);
  }, []);

  const verifyEligibility = async (obj: any) => {
    let parameters: IVerifyEligibilityRequest;
    const membershipNumber = obj['membershipNumber'];
    const zipCode = obj['zipCode'];

    try {
      parameters = {
        membershipNumber,
        zipCode,
        appFlow,
        brokerRegion,
      };
      setLoading(true);
      const {
        amountDue,
        customerId,
        primaryEmail,
        autoPay,
        membershipLevel,
        campaignCode,
        productCost,
        preDiscountAmountDue,
        taxAmount,
        clubCode,
        status,
        membershipProductRV,
        billPlan,
      } = await sources.verifyEligibility(parameters);

      if (status === StatusCode.SuccessAlreadyReported) {
        setErrorType(ErrorType.PAYMENT_IN_PROCESSING);
        setError(true);
        setLoading(false);
      } else if (appFlow === AppFlow.AUTOPAY && !autoPay?.eligible && amountDue > 0) {
        setClubCode(clubCode);
        setErrorType(ErrorType.INELIGIBLE_FOR_AUTOPAY);
        setError(true);
        setLoading(false);
      } else {
        setError(false);
        setEmailOnFile(primaryEmail);
        setLoading(false);
        setMembershipNumber(membershipNumber);
        setZipCode(zipCode);
        setAmountDue(amountDue);
        setMembershipLevel(membershipLevel);
        setMembershipProductRV(membershipProductRV);
        setCampaignCode(campaignCode);
        setAutoPay(autoPay);
        setProductCost(productCost);
        setPreDiscountAmountDue(preDiscountAmountDue);
        setTaxAmount(taxAmount);
        setGlobalClubCode(clubCode);
        setBillPlan(billPlan);
        setCustomerId(customerId);

        const isStandaloneAutoPay = !amountDue && autoPay?.eligible;
        if (isStandaloneAutoPay) {
          navigate(Pages.AutoPay);
        } else {
          navigate(Pages.Review);
        }
      }
    } catch (error: any) {
      const isBotDetectionResponse = error.type === ErrorType.BOT_ERROR;
      if (isBotDetectionResponse) {
        setError(false);
        window._pxOnCaptchaSuccess = (isValid) => {
          if (isValid) {
            verifyEligibility(obj);
          }
        };
      } else {
        const errorType = error.type;
        const clubCode = error?.data?.clubCode || '004'; // Use fallback club code
        setClubCode(clubCode);
        setErrorType(errorType);
        setError(true);
        setLoading(false);
      }
    }
  };

  if (tagging) {
    return <></>;
  }

  return isLoading ? (
    <Spinner />
  ) : (
    <VerifyEligibilityForm
      errorType={errorType}
      hasError={hasError}
      onSubmit={verifyEligibility}
      clubCode={clubCode}
      appFlow={appFlow}
      initialMembershipNumber={membershipNumber}
      initialZipCode={zipCode}
    />
  );
};

export default VerifyEligibilityPage;
